import { API_URL } from '../environment';

import fetcher from '../utils/fetcher';

import SearchResult, { SubscriptionData } from '../model/SearchResult';
import ControlUnitMode from '../model/ControlUnitMode';
import UserCompany from '../model/UserCompany';
import Receipt from '../model/Receipt';
import Timestamp from '../model/Timestamp';
import { Product } from '../model/Product';
import { PaymentMethod, VivaWalletPaymentMethod } from '../model/PaymentMethod';
import { User } from '../model/User';
import Company from '../model/Company';
import CreateCompanyDto from '../model/CreateCompanyDto';
import { CreateVivaAccountDTO, VivaAccountStatus } from '../model/Viva';
import Printer from '../model/Printer';
import { Money } from '../utils/currency';
import { Currency } from '../constants/currency';
import { BillingItem } from '../model/BillingItem';
import { Invoice } from '../model/Invoice';
import { AdminSubscription, Subscription } from '../model/subscription';
import { DataPointResponse } from '../components/statistics/charts/types';

export const getUserPermissions = async (uid: string): Promise<User> =>
  fetcher(`${API_URL}/v1/user/${uid}/permissions`, {
    method: 'GET',
  });

export const searchUser = async (searchString: string): Promise<SearchResult[]> =>
  fetcher(`${API_URL}/v1/admin/search?search=${encodeURIComponent(searchString)}`, {
    method: 'GET',
  });

export const updateCompany = async (
  companyKey: string,
  subscriptions: SubscriptionData[],
  subscriptionExpires?: Timestamp,
  controlUnitMode?: ControlUnitMode,
): Promise<SearchResult[]> =>
  fetcher(`${API_URL}/v1/admin/save/${companyKey}`, {
    method: 'POST',
    data: JSON.stringify({
      controlUnitMode,
      subscriptionExpires,
      subscriptions,
    }),
  });

export const updateResellerCompanySubscription = async (
  companyKey: string,
  resellerId: string,
  subscriptions: SubscriptionData[],
): Promise<SearchResult[]> =>
  fetcher(`${API_URL}/v1/reseller/${resellerId}/companies/${companyKey}/subscription`, {
    method: 'PUT',
    data: JSON.stringify(subscriptions),
  });

export const getRegister = async (companyId: string): Promise<unknown> =>
  fetcher(`${API_URL}/v1/company/${companyId}/register`, {
    method: 'GET',
  });

export const replaceCompanyControlUnit = async (
  companyId: string,
  controlUnitSerial: string,
): Promise<{ registerKey: string; controlUnitSerial: string }> =>
  fetcher(`${API_URL}/v1/admin/companies/${companyId}/register/replace-control-unit`, {
    method: 'POST',
    data: JSON.stringify({ controlUnitSerial }),
  });

export const getUserCompanies = async (): Promise<UserCompany[]> =>
  fetcher(`${API_URL}/v1/company/`, {
    method: 'GET',
  });

export const getCompany = async (companyKey: string): Promise<SearchResult> =>
  fetcher(`${API_URL}/v1/company/${companyKey}`, {
    method: 'GET',
  });

export const updateCompanyInfo = async (
  companyId: string,
  companyData: Partial<Company>,
): Promise<Company> =>
  fetcher(`${API_URL}/v1/company/${companyId}`, {
    method: 'PUT',
    data: JSON.stringify({
      profileChanges: companyData,
    }),
  });

export const getReceiptList = async (
  companyKey: string,
  offset = 0,
  limit = 25,
): Promise<Receipt[]> =>
  fetcher(`${API_URL}/v1/company/${companyKey}/receipts?offset=${offset}&limit=${limit}`, {
    method: 'GET',
  });

export const getProductList = async (
  companyKey: string,
  offset = 0,
  limit = 25,
): Promise<Product[]> =>
  fetcher(`${API_URL}/v1/company/${companyKey}/products?offset=${offset}&limit=${limit}`, {
    method: 'GET',
  }).then((res) => {
    return res.map((product: Product) => ({ ...product, price: new Money(product.price) }));
  });

export const getPrinters = async (companyKey: string): Promise<Printer[]> =>
  fetcher(`${API_URL}/v1/company/${companyKey}/printers/`, {
    method: 'GET',
  });

export const addPrinterReq = async (
  companyId: string,
  body: Omit<Printer, 'id'>,
): Promise<Printer> =>
  fetcher(`${API_URL}/v1/company/${companyId}/printers`, {
    method: 'POST',
    data: JSON.stringify(body),
  });

export const removePrinterReq = async (companyId: string, printerId: string) =>
  fetcher(`${API_URL}/v1/company/${companyId}/printers/${printerId}`, {
    method: 'DELETE',
  });

export const editPrinterReq = async (companyId: string, body: Printer) =>
  fetcher(`${API_URL}/v1/company/${companyId}/printers/${body.id}`, {
    method: 'PUT',
    data: JSON.stringify(body),
  });

export const removeProductReq = async (companyId: string, productId: string) =>
  fetcher(`${API_URL}/v1/company/${companyId}/products/${productId}`, {
    method: 'DELETE',
  });

export const editProductReq = async (companyId: string, body: Product) => {
  return fetcher(`${API_URL}/v1/company/${companyId}/products/${body.id}`, {
    method: 'POST',
    data: JSON.stringify(body),
  }).then(
    (product: Product): Product => ({
      ...product,
      price: new Money(product.price),
    }),
  );
};

export const getVouchers = async (companyKey: string, offset = 0, limit = 25) =>
  fetcher(
    `${API_URL}/v1/admin/companies/${companyKey}/bookkeepingVouchers?offset=${offset}&limit=${limit}`,
    {
      method: 'GET',
    },
  );

export const getBookkeepingIntegration = async (companyKey: string) =>
  fetcher(`${API_URL}/v1/bookkeeping/integration/${companyKey}`, {
    method: 'GET',
  });

export const getBookkeepingAccounts = async (companyId: string) =>
  fetcher(`${API_URL}/v1/bookkeeping/company/${companyId}/accounts`, {
    method: 'GET',
  });

export const retryVoucher = async (companyKey: string, voucherKey: string) =>
  fetcher(`${API_URL}/v1/bookkeeping/company/${companyKey}/voucher/${voucherKey}/retry`, {
    method: 'POST',
  });

export const getZReportHistory = async (
  companyKey: string,
): Promise<{
  reports: {
    id: string;
    time: Timestamp;
  }[];
}> =>
  fetcher(`${API_URL}/v1/company/${companyKey}/z-history`, {
    method: 'GET',
  });

export interface RegisterZReportHistory {
  registerKey: string;
  label: string;
  serial?: string;
  controlUnitSerial?: string;
  activatedAt?: Timestamp;
  isCurrent: boolean;
  reports: { id: string; time: Timestamp }[];
}

export const getRegisterZReportHistory = async (
  companyKey: string,
): Promise<{ registers: RegisterZReportHistory[] }> =>
  fetcher(`${API_URL}/v1/company/${companyKey}/z-history?includeHistory=true`, {
    method: 'GET',
  });

export const getZReport = async (
  companyKey: string | undefined,
  reportId: string,
  registerKey?: string,
) =>
  fetcher(
    `${API_URL}/v1/company/${companyKey}/z-report/${encodeURIComponent(reportId)}${
      registerKey ? `?registerKey=${encodeURIComponent(registerKey)}` : ''
    }`,
    {
      method: 'GET',
    },
  );

export const getSubscriptions = async (limit: number, offset = 0): Promise<AdminSubscription[]> =>
  fetcher(`${API_URL}/v1/admin/subscriptions?manual=true&limit=${limit}&offset=${offset}`, {
    method: 'GET',
  });

export const getPaymentMethods = async (companyId: string): Promise<PaymentMethod[]> =>
  fetcher(`${API_URL}/v1/company/${companyId}/paymentMethods`, {
    method: 'GET',
  });

export const addPaymentMethod = async (companyId: string, paymentMethod: Partial<PaymentMethod>) =>
  fetcher(`${API_URL}/v1/company/${companyId}/paymentMethods`, {
    method: 'POST',
    data: JSON.stringify(paymentMethod),
  });

export const removePaymentMethod = async (companyId: string, paymentMethodId: string) =>
  fetcher(`${API_URL}/v1/company/${companyId}/paymentMethods/${paymentMethodId}`, {
    method: 'DELETE',
  });

export const updatePaymentMethod = async (
  companyId: string,
  paymentMethodId: string,
  paymentMethod: Partial<PaymentMethod>,
) =>
  fetcher(`${API_URL}/v1/company/${companyId}/paymentMethods/${paymentMethodId}`, {
    method: 'POST',
    data: JSON.stringify(paymentMethod),
  });

export const getResellers = async () =>
  fetcher(`${API_URL}/v1/reseller`, {
    method: 'GET',
  });

export const updateReseller = async (companyId: string, resellerId: string) =>
  fetcher(`${API_URL}/v1/company/${companyId}/reseller`, {
    method: 'POST',
    data: { resellerId },
  });

export const getResellerCompanies = async (
  resellerId: string,
  lastDocument?: string,
  limit = 10,
): Promise<Company[]> => {
  let URL = `${API_URL}/v1/reseller/${resellerId}/companies?limit=${limit}`;

  if (lastDocument) {
    URL += `&lastDocument=${lastDocument}`;
  }

  return fetcher(URL, {
    method: 'GET',
  });
};

export const searchResellerCompanies = async (query: string): Promise<Company[]> =>
  fetcher(`${API_URL}/v1/reseller/companies/search/?search=${encodeURIComponent(query)}`, {
    method: 'GET',
  });

export const createCompany = async (body: CreateCompanyDto): Promise<Company> =>
  fetcher(`${API_URL}/v1/admin/createUser`, {
    method: 'POST',
    data: JSON.stringify(body),
  });

export const createResellerCompany = async (
  resellerId: string,
  body: CreateCompanyDto,
): Promise<Company> =>
  fetcher(`${API_URL}/v1/reseller/${resellerId}/companies`, {
    method: 'POST',
    data: JSON.stringify(body),
  });

export const createVivaAccount = async (
  companyId: string,
  paymentMethod: CreateVivaAccountDTO,
): Promise<{ paymentMethod: VivaWalletPaymentMethod }> =>
  fetcher(`${API_URL}/v1/viva/${companyId}`, {
    method: 'POST',
    data: JSON.stringify(paymentMethod),
  });

export const getVivaAccountStatus = async (companyId: string): Promise<VivaAccountStatus> =>
  fetcher(`${API_URL}/v1/viva/${companyId}/status`, { method: 'GET' });

export const getSubscriptionsReport = async (): Promise<string> =>
  fetcher(`${API_URL}/v1/admin/subscription-report`, { method: 'GET' });

export const addUser = async (companyId: string, email: string, password?: string): Promise<void> =>
  fetcher(`${API_URL}/v1/company/${companyId}/users`, {
    method: 'POST',
    data: JSON.stringify({ email, password }),
  });

export const getBillingItems = async (companyId: string): Promise<BillingItem[]> =>
  fetcher(`${API_URL}/v1/company/${companyId}/billingItems`, {
    method: 'GET',
  }).then((response: BillingItem[]) => {
    return response.map((item: BillingItem) => ({
      ...item,
      unitPrice: new Money(item.unitPrice, Currency.SEK),
    }));
  });

export const createBillingItem = async (
  companyId: string,
  billingItem: Omit<BillingItem, 'createdAt' | 'id' | 'createdBy' | 'discount' | 'taxRate'>,
): Promise<void> => {
  await fetcher(`${API_URL}/v1/company/${companyId}/billingItems`, {
    method: 'POST',
    data: JSON.stringify(billingItem),
  });
};

export const updateBillingItem = async (
  companyId: string,
  billingItemId: string,
  billingItem: Partial<BillingItem>,
): Promise<void> => {
  await fetcher(`${API_URL}/v1/company/${companyId}/billingItems/${billingItemId}`, {
    method: 'PATCH',
    data: JSON.stringify(billingItem),
  });
};

export const deleteBillingItem = async (companyId: string, billingItemId: string): Promise<void> =>
  fetcher(`${API_URL}/v1/company/${companyId}/billingItems/${billingItemId}`, {
    method: 'DELETE',
  });

export const getInvoices = async (companyId: string): Promise<Invoice[]> =>
  fetcher(`${API_URL}/v1/company/${companyId}/invoices`, {
    method: 'GET',
  });

export const generateCompanyInvoice = async (companyId: string, month: string): Promise<void> => {
  return fetcher(`${API_URL}/v1/company/${companyId}/invoices/generate`, {
    method: 'POST',
    data: JSON.stringify({ month }),
  });
};

export const getResellerInvoices = async (resellerId: string): Promise<Invoice[]> => {
  return fetcher(`${API_URL}/v1/reseller/${resellerId}/invoices`, {
    method: 'GET',
  });
};

export const createSubscription = async (
  companyId: string,
  subscriptionData: {
    subscriptionId: string;
    startDate: string;
    endDate?: string;
  },
): Promise<void> => {
  return fetcher(`${API_URL}/v1/company/${companyId}/subscriptions`, {
    method: 'POST',
    data: JSON.stringify(subscriptionData),
  });
};

export const updateSubscription = async (
  companyId: string,
  subscriptionId: string,
  subscriptionData: {
    endDate?: string;
  },
): Promise<void> => {
  return fetcher(`${API_URL}/v1/company/${companyId}/subscriptions/${subscriptionId}`, {
    method: 'PATCH',
    data: JSON.stringify(subscriptionData),
  });
};

export const getActiveSubscriptions = async (companyId: string): Promise<Subscription[]> => {
  return fetcher(`${API_URL}/v1/company/${companyId}/subscriptions`, {
    method: 'GET',
  });
};

export const generateResellerInvoice = async (resellerId: string): Promise<void> => {
  return fetcher(`${API_URL}/v1/reseller/${resellerId}/invoices/generate`, {
    method: 'POST',
  });
};

export const sendResellerInvoiceToFortnox = async (
  resellerId: string,
  invoiceId: string,
): Promise<void> => {
  return fetcher(`${API_URL}/v1/reseller/${resellerId}/invoices/${invoiceId}/send-to-fortnox`, {
    method: 'POST',
  });
};

export const sendInvoiceToFortnox = async (companyId: string, invoiceId: string): Promise<void> => {
  return fetcher(`${API_URL}/v1/company/${companyId}/invoices/${invoiceId}/send-to-fortnox`, {
    method: 'POST',
  });
};

export type RawPrices = {
  subscriptions: {
    [subscriptionId: string]: {
      price: Money;
      discount: number;
      taxRate: number;
    };
  };
  recurring: {
    [recurringId: string]: {
      price: Money;
      discount: number;
      taxRate: number;
    };
  };
};

export const getPrices = async (): Promise<RawPrices> => {
  return fetcher(`${API_URL}/v1/prices`, {
    method: 'GET',
  });
};

export const getResellerPrices = async (resellerId: string): Promise<RawPrices> => {
  return fetcher(`${API_URL}/v1/prices/reseller/${resellerId}/`, {
    method: 'GET',
  });
};

export const createCustomerInFortnox = async (companyId: string): Promise<void> => {
  return fetcher(`${API_URL}/v1/admin/companies/${companyId}/create-customer-in-fortnox`, {
    method: 'POST',
  });
};

export const integrateBookkeeping = async (bookkeeper: string, companyId: string) =>
  fetcher(`${API_URL}/v1/webshop/bookkeeping/integrate/${bookkeeper}/${companyId}`, {
    method: 'GET',
  });

export const getDailyStats = async (
  startDate: string,
  endDate: string,
): Promise<DataPointResponse[]> =>
  fetcher(
    `${API_URL}/v1/admin/stats/daily?startDate=${encodeURIComponent(
      startDate,
    )}&endDate=${encodeURIComponent(endDate)}`,
    {
      method: 'GET',
    },
  );
